import { Capsule, LAYER_ORDER } from '../types/capsule';
import '../styles/CapsuleCard.css';

interface CapsuleCardProps {
  capsule: Capsule;
  onEdit: (capsule: Capsule) => void;
  onDelete: (capsule: Capsule) => void;
}

function formatRelativeDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'just now'; // handle clock skew / future dates
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 14) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function CapsuleCard({ capsule, onEdit, onDelete }: CapsuleCardProps) {
  const { metadata, layers, updatedAt } = capsule;
  const difficulty = metadata.difficulty ?? 1;
  const clampedDifficulty = Math.max(0, Math.min(5, difficulty));

  return (
    <div className="capsule-card">
      <div className="capsule-card-header">
        <h3 className="capsule-card-title">{metadata.title}</h3>
        {metadata.artist && (
          <p className="capsule-card-artist">{metadata.artist}</p>
        )}
      </div>

      <div className="capsule-card-difficulty">
        <span className="difficulty-stars">
          {'★'.repeat(clampedDifficulty)}{'☆'.repeat(5 - clampedDifficulty)}
        </span>
        <span className="difficulty-label">Difficulty {clampedDifficulty}</span>
      </div>

      <div className="capsule-card-layers">
        {LAYER_ORDER.map((layer) => {
          const hasLayer = layers[layer] !== undefined;
          return (
            <span
              key={layer}
              className={`layer-dot ${hasLayer ? 'filled' : 'empty'}`}
              data-testid={hasLayer ? 'layer-indicator-filled' : 'layer-indicator-empty'}
              title={layer}
            />
          );
        })}
      </div>

      <div className="capsule-card-date">
        Last edited: {formatRelativeDate(updatedAt)}
      </div>

      <div className="capsule-card-actions">
        <button
          className="btn btn-secondary"
          onClick={() => onEdit(capsule)}
        >
          Edit
        </button>
        <button
          className="btn btn-danger"
          onClick={() => onDelete(capsule)}
        >
          Delete
        </button>
      </div>
    </div>
  );
}
